import { NgClass, NgStyle } from '@angular/common';
import { Component, inject, Input, output, signal } from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { ImportGrundAdmin, ImportGrundAdminCmd } from '@app/shared/classes/import-grund';
import { BackendService } from '@app/shared/services/backend.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import Swal from 'sweetalert2';

@Component({
  // tslint:disable-next-line:component-selector
  selector: '[app-admin-stammdaten-importgrund-entry]',
  templateUrl: './admin-stammdaten-importgrund-entry.component.html',
  imports: [
    NgClass,
    NgStyle,
    ReactiveFormsModule,
    TranslatePipe,
  ],
})
export class AdminStammdatenImportgrundEntryComponent {
  private readonly backendService = inject(BackendService);
  private readonly translateService = inject(TranslateService);
  private readonly formBuilder = inject(UntypedFormBuilder);

  readonly deleted = output();
  readonly reloadEntries = output();

  protected readonly grundForm = signal(this.generateForm());
  protected readonly delChosen = signal(false);

  private grund: ImportGrundAdmin | null = null;

  private generateForm(): UntypedFormGroup {
    return this.formBuilder.group({
      bezeichnungDe          : [this.grund?.bezeichnungDe          ?? '', Validators.required],
      bezeichnungFr          : [this.grund?.bezeichnungFr          ?? '', Validators.required],
      bezeichnungIt          : [this.grund?.bezeichnungIt          ?? '', Validators.required],
      sortierung             : [this.grund?.sortierung             ?? '', Validators.required],
      kommentarPflicht       : [this.grund?.kommentarPflicht       ?? ''],
      zulassungsNrChAnzeigen : [this.grund?.zulassungsNrChAnzeigen ?? ''],
      lieferEngpassAnzeigen  : [this.grund?.lieferEngpassAnzeigen  ?? ''],
      aktiv                  : [this.grund?.aktiv                  ?? '']
    });
  }

  @Input()
  set set(grund: ImportGrundAdmin) {
    if (!grund) {
      throw new Error('ImportGrund must exist');
    }
    this.grund = grund;
    this.reset();
  }

  protected submit() {
    if (this.grund) {
      const formValues = this.grundForm().value;
      const cmd = new ImportGrundAdminCmd({
        id                     : this.grund.id,
        bezeichnungDe          : formValues.bezeichnungDe,
        bezeichnungFr          : formValues.bezeichnungFr,
        bezeichnungIt          : formValues.bezeichnungIt,
        sortierung             : formValues.sortierung,
        kommentarPflicht       : formValues.kommentarPflicht,
        zulassungsNrChAnzeigen : formValues.zulassungsNrChAnzeigen,
        lieferEngpassAnzeigen  : formValues.lieferEngpassAnzeigen,
        aktiv                  : formValues.aktiv
      });
      this.backendService.editImportgrund(cmd)
        .subscribe({
          next: result => {
            this.grund = result;
            this.reloadEntries.emit();
          },
          error: console.error
        });
    }
  }

  protected reset() {
    this.grundForm.set(this.generateForm());
  }

  protected confirmDelete() {
    const grund = this.grund;
    if (grund) {
      this.delChosen.set(true);
      Swal.fire({
        text              : this.translateService.instant('admin.importgrund.delete.warning'),
        icon              : 'warning',
        buttonsStyling    : true,
        showCancelButton  : true,
        confirmButtonText : this.translateService.instant('admin.importgrund.delete.yes'),
        cancelButtonText  : this.translateService.instant('global.cancel')
      }).then((result) => {
        if (result.value) {
          this.delete(grund.id);
        } else {
          this.delChosen.set(false);
        }
      });
    }
  }

  private delete(grundId: number) {
    this.backendService.deleteImportgrund(grundId)
      .subscribe({
        next: () => {
          this.showAlert(true);
          this.deleted.emit();
        },
        error: error => {
          this.showAlert(false);
          console.error(error.message);
        }
      });
  }

  private showAlert(success: boolean) {
    const msgCode = success
      ? 'admin.importgrund.delete.success'
      : 'admin.importgrund.delete.error';

    Swal.fire({
      text              : this.translateService.instant(msgCode),
      icon              : success ? 'success' : 'error',
      buttonsStyling    : true,
      confirmButtonText : this.translateService.instant('global.ok')
    });
  }
}
